import type { CreditStatus } from '@dcc-erp/shared';
import { fmt } from '@dcc-erp/shared';

// Credit-limit bar for the create-invoice modal (AR-08). Renders the customer's
// current exposure (used) against their creditLimit plus the available headroom,
// and — when the in-progress invoice would push projected exposure over the
// limit — a warning row. Renders NOTHING when creditLimit is 0 (unlimited): the
// caller still owns submit-gating, this is the read-only visual.
interface Props {
  status: CreditStatus;
  /** The modal's already-computed VAT-inclusive invoice total. */
  draftTotal: number;
  /** exposure + draftTotal > creditLimit (computed by the caller, who also gates submit). */
  projectedOver: boolean;
}

export function CreditLimitBar({ status, draftTotal, projectedOver }: Props) {
  const creditLimit = Number(status.creditLimit);
  const exposure = Number(status.exposure);
  // creditLimit 0 ⇒ unlimited; render nothing (caller never blocks either).
  if (!(creditLimit > 0)) return null;

  const available = Number(status.available);
  // Used-fraction of the bar (current exposure). Clamp to [0,1] for the fill.
  const usedPct = Math.max(0, Math.min(100, (exposure / creditLimit) * 100));

  return (
    <div
      data-testid="ar-credit-bar"
      style={{
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: '10px 12px',
        fontSize: 13,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        background: projectedOver ? 'var(--warning-bg, #fff7ed)' : '#fafafa',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span style={{ fontWeight: 600 }}>วงเงินเครดิต</span>
        <span className="tnum muted">
          ใช้ {fmt(exposure, 2)} / {fmt(creditLimit, 2)}
        </span>
      </div>
      <div
        style={{
          height: 8,
          borderRadius: 999,
          background: 'var(--border)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${usedPct}%`,
            height: '100%',
            background: projectedOver ? 'var(--negative)' : 'var(--positive, #16a34a)',
          }}
        />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span className="muted">คงเหลือ</span>
        <span className="tnum">{fmt(available, 2)}</span>
      </div>
      {projectedOver && (
        <div
          data-testid="ar-credit-over-warning"
          style={{ color: 'var(--negative)', fontWeight: 600 }}
        >
          ⚠ เกินวงเงิน: ใช้ {fmt(exposure, 2)} + ใบนี้ {fmt(draftTotal, 2)} &gt; วงเงิน{' '}
          {fmt(creditLimit, 2)}
        </div>
      )}
    </div>
  );
}
